import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { IconButton, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

interface AudioPreviewContextValue {
  currentId: string | null;
  isPlaying: boolean;
  toggle: (id: string, resolveUrl: () => Promise<string | null>) => void;
}

const AudioPreviewContext = createContext<AudioPreviewContextValue | null>(null);

/**
 * App-level single-track audio preview (one shared <audio>). Lets the library /
 * clip lists play one track at a time without a full player. Mounted once in the
 * app shell so playback survives navigation between library pages.
 */
export function AudioPreviewProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const toggle = useCallback(
    (id: string, resolveUrl: () => Promise<string | null>) => {
      const audio = audioRef.current;
      if (!audio) return;
      if (currentId === id) {
        if (audio.paused) void audio.play().catch(() => undefined);
        else audio.pause();
        return;
      }
      void resolveUrl()
        .then((url) => {
          if (!url) return;
          audio.src = url;
          setCurrentId(id);
          return audio.play();
        })
        .catch(() => undefined);
    },
    [currentId],
  );

  return (
    <AudioPreviewContext.Provider value={{ currentId, isPlaying, toggle }}>
      {children}
      <audio
        ref={audioRef}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
      />
    </AudioPreviewContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAudioPreview(): AudioPreviewContextValue {
  const ctx = useContext(AudioPreviewContext);
  if (!ctx) throw new Error('useAudioPreview must be used within AudioPreviewProvider');
  return ctx;
}

/** Play/pause toggle for a single track/clip. `resolveUrl` is called lazily on first play. */
export function PreviewButton({
  id,
  resolveUrl,
  size = 'sm',
}: {
  id: string;
  resolveUrl: () => Promise<string | null>;
  size?: string;
}) {
  const { t } = useTranslation();
  const { currentId, isPlaying, toggle } = useAudioPreview();
  const active = currentId === id && isPlaying;
  return (
    <IconButton
      aria-label={active ? t('player.pause') : t('player.play')}
      size={size}
      variant="ghost"
      color="gray.500"
      _hover={{ color: 'brand.500', bg: 'brand.50' }}
      icon={<Text fontSize="sm" lineHeight={1}>{active ? '⏸' : '▶'}</Text>}
      onClick={(e) => {
        e.stopPropagation();
        toggle(id, resolveUrl);
      }}
    />
  );
}
